import { useState } from 'react'

import { useCreateCheckpoint } from '../hooks/useCreateCheckpoint'
import { useLightMode } from '@/hooks/useLightMode'
import { useRouter } from 'next/compat/router'
import { useTranslation } from 'react-i18next'

import { Box as DarkBox, Modal, TextInput } from '@hivewatch/dark'

interface DarkCreateCheckPointsModalIProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Dark twin of CreateCheckPointsModal. The legacy form went through
 * react-hook-form to drive two uncontrolled inputs; the dark TextInput is
 * controlled, so the same "name required, facility read-only" contract is
 * expressed with local state instead.
 */
export const DarkCreateCheckPointsModal = ({
  isOpen,
  onClose,
}: DarkCreateCheckPointsModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { query } = useRouter()
  const selectedFloorId = Array.isArray(query?.floorId)
    ? query?.floorId[0]
    : query?.floorId

  const [name, setName] = useState('')
  const [hasBlurred, setHasBlurred] = useState(false)

  const {
    create,
    handleOnClose,
    isCreateDeviceLoading,
    isDataLoading,
    facilityName,
  } = useCreateCheckpoint({ floorId: selectedFloorId, onClose })

  const isNameEmpty = !name.trim()

  return (
    <Modal
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDisabled={isDataLoading || isNameEmpty || !facilityName}
      isSaveLoading={isCreateDeviceLoading}
      onClose={handleOnClose}
      onSave={() => create(name)}
      saveButtonLabel={t('common:create')}
      showCancelButton
      size='compact'
      title={t('floors:checkpoint_create_button')}
    >
      <DarkBox
        data-testid='DarkCreateCheckPointsModal:body'
        display='flex'
        flexDirection='column'
        gap='16px'
      >
        <TextInput
          colorScheme={colorScheme}
          data-testid='DarkCreateCheckPointsModal:name'
          hasWarning={hasBlurred && isNameEmpty}
          label={t('floors:checkpoint_edit_checkpoint_name_label')}
          onBlur={() => setHasBlurred(true)}
          onValueChange={setName}
          placeholder={t('floors:checkpoint_create_name_placeholder')}
          value={name}
          warningText={t('floors:checkpoint_create_name_required')}
          width='100%'
        />
        <TextInput
          colorScheme={colorScheme}
          data-testid='DarkCreateCheckPointsModal:facility'
          disabled
          label={t('floors:checkpoint_facility_label')}
          placeholder={t('floors:checkpoint_create_facility_placeholder')}
          value={facilityName ?? ''}
          width='100%'
        />
      </DarkBox>
    </Modal>
  )
}
